import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  Input,
  PLATFORM_ID,
  signal,
  WritableSignal,
} from "@angular/core";
import { Store, select } from "@ngrx/store";
import { toSignal } from "@angular/core/rxjs-interop";
import { CoreSelectors } from "../../store/selectors";
import { isPlatformBrowser } from "@angular/common";

interface AnchorLink {
  id: string;
  title: string | undefined;
}

@Component({
  selector: "audio-anchor-scroll",
  templateUrl: "./anchor-scroll.component.html",
  styleUrls: ["./anchor-scroll.component.sass"],

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnchorScrollComponent implements AfterViewInit {
  private document = inject(DOCUMENT);
  private store = inject<Store>(Store);
  private platformId = inject(PLATFORM_ID);
  private elementRef = inject(ElementRef);

  @Input() selector!: string;

  public $links: WritableSignal<AnchorLink[]> = signal([]);
  public $scrollIndex: WritableSignal<number> = signal(0);
  public $fragment = signal<string | null>(null);

  $scrollPosition = toSignal(
    this.store.pipe(select(CoreSelectors.getScrollPosition)),
    { initialValue: 0 },
  );

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      effect(() => {
        if (this.$links().length) {
          // console.log(this.$links());
          this.computeScrollIndex(this.$scrollPosition());
        }
      });
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const anchors = this.document.querySelectorAll<HTMLAnchorElement>(this.selector);

      // console.log(anchors);

      this.$links.set(
        Array.from(anchors).map((block) => ({
          id: block.id,
          title: block.dataset.title,
        })),
      );

      this.elementRef.nativeElement.style.setProperty(
        `--anchor_elements_count`,
        anchors.length,
      );
    }

    if (this.$fragment() && isPlatformBrowser(this.platformId)) {
      this.scrollTo(this.$fragment()!);
    }
  }

  private computeScrollIndex(scrollPosition: number): void {
    const blocks = this.document.querySelectorAll<HTMLElement>(this.selector);

    const scrollOffsets = Array.from(blocks)
      .map((block) => block.offsetTop)
      .sort((a, b) => a - b);

    for (let i = 0; i < scrollOffsets.length; i++) {
      const offsetLow = scrollOffsets[i];
      const offsetHigh = scrollOffsets[i + 1];

      if (
        typeof scrollOffsets[i + 1] !== "undefined" &&
        offsetLow <= scrollPosition &&
        scrollPosition < offsetHigh
      ) {
        this.$scrollIndex.set(i);
      } else if (offsetLow <= scrollPosition) {
        this.$scrollIndex.set(scrollOffsets.length - 1);
      }
    }

    // console.log("scroll position : " + this.$scrollPosition());
    // console.log("scroll index : " + this.$scrollIndex());
  }

  scrollTo(fragment: string): void {
    this.$fragment.set(fragment);
    // this.router.navigate(["."], {
    //   fragment,
    //   // skipLocationChange: true,

    //   // onSameUrlNavigation: "ignore",
    // });

    const element = this.document.querySelector("#" + fragment);

    if (element) {
      element.scrollIntoView();
    }
  }
}
